import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { isAxiosError } from "axios";
import { differenceInMilliseconds } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from 'react-router-dom';
import { toast } from "react-toastify";
import { useProviderAuthState } from '../../api/hooks/provider/auth/useProviderAuthState';
import { useCancelAppointment } from '../../api/hooks/provider/useCancelAppointment';
import { useGetPatientFormResponse } from '../../api/hooks/provider/useGetPatientResponse';
import { useGetProviderAppointmentCount } from '../../api/hooks/provider/useGetProviderApointmentCount';
import { useGetProviderForm } from '../../api/hooks/provider/useGetProviderForm';
import { useGetProviderProfileDetails } from '../../api/hooks/provider/useGetProviderProfileDetails';
import { useGetPatientUpcomingAppointments } from '../../api/hooks/provider/useGetProviderUpcomingAppointsments';
import { useGetSinglePatientRecord } from "../../api/hooks/provider/useGetSinglePatientRecord";
import { useGetProviderPatientsCount } from "../../api/hooks/provider/useProviderPatientsCount";
import { useSaveChargeSheetDraft } from "../../api/hooks/provider/useSaveChargeSheet";
import { useSaveFormResponse } from "../../api/hooks/provider/useSaveFormResponse";
import { useSendChargeSheetToBilling } from "../../api/hooks/provider/useSendChargeSheetToBilling";
import { UpcomingAppointmentType } from "../../api/types";
import ReusableTable from "../../components/ReusableTable";
import FormBuilder from "../../components/form/FormBuilder";
import SearchField from "../../components/form/SearchField";
import { VideoModal } from "../../components/introVideoModal";
import { TempleMeeting } from "../../components/meetings/TempleProviderMeeting";
import { usePostMeeting } from "../../components/meetings/hooks/postMeeting";
import Button from "../../components/shared/Button";
import ListTabsWidget from "../../components/shared/ListTabsWidget";
import { Loading } from "../../components/shared/Loading";
import ModalPortal from '../../components/shared/ModalPortal';
import { CancelIcon } from "../../svgs/CancelIcon";
import { Playicon } from "../../svgs/PlayIcon";
import { ReactComponent as EmptyMsgIcon } from "../../svgs/empty-smg.svg";
import { Appointment } from "../../types/appointment";
import { NoteValue } from "../pages/PatientDetail/interfaces";
import { SimpleChargeSheetModal } from "./SimpleModal";
import MobileAppointmentCard from "./appointments/MobileAppointmentCard";
import ProviderAppointmentsDrawer from "./appointments/ProviderAppointmentDrawer";
import RescheduleAppointmentModal from "./appointments/RescheduleAppointment";
import AppointmentsCountCard from "./dashboard/AppointmentsCountCard";
import EventCard from "./dashboard/EventCard";
import PatientsCountCard from "./dashboard/PatientsCountCard";
import ProviderDashboardLayout from "./dashboard/ProviderDashboardLayout";
import { useStartAwsScribe } from "./hooks/use_aws_scribe";
import { useOverViewTableCols } from "./table/hooks/use_overview_cols";
import { useCardDetails } from "../../context/CardDetailsContext";
import http from "../../utils/http";
import { useStripeCardDetails } from "../../hooks/useStripeCardDetails";
import { ChargesModal } from "../../components/meetings/ChargesModal";

const ProviderDashboardOverview = () => {
  const location = useLocation();
  // Check whether the user is coming from the login or professional-details page so they can be shown the intro video modal
  const fromLoginOrProfessionalDetails = [
    "/professional-details",
    "/login"
  ].includes(location.state?.from);
  const { user } = useProviderAuthState();
  const [upcomingAppointmentsNow, setUpcomingAppointmentNow] = useState<
    UpcomingAppointmentType[]
  >([]);
  const [appointmentInfo, setAppointmentInfo] = useState<Appointment | null>(
    null
  );
  const [openAppointmentDetails, setOpenAppointmentDetails] =
    useState<boolean>(false);
  const [joinCall, setJoinCall] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeVideoTabLabel, setActiveVideoTabLabel] = useState<string>("Notes");
  const [showChargeSheetModal, setShowChargeSheetModal] =
    useState<boolean>(false);
  const [showMeetingCostModal, setshowMeetingCostModal] =
    useState<boolean>(false);
  const [isRescheduleViewVisible, setIsRescheduleViewVisible] =
    useState<boolean>(false);
  const [oldAppointmentForReschedule, setOldAppointmentForReschedule] =
    useState("");
  const [appointmentId, setAppointmentId] = useState<string>("");
  const [isThirtyMinutesBefore, setIsThirtyMinutesBefore] =
    useState<boolean>(false);
  const [patientId, setPatientId] = useState<string>("");
  const [noteFields, setNoteFields] = useState<NoteValue>({});
  const [isIntroVideo, setIsIntroVideo] = useState<boolean>(
    fromLoginOrProfessionalDetails
  );
  const [noteGenerationStatus, setNoteGenerationStatus] = useState<"ai-generation" | "manual">("manual");
  const [fileName, setFileName] = useState<any>(null);
  const [showAIPricingModal, setShowAIPricingModal] = useState(false);
  const [didCallEnd, setDidCallEnd] = useState<boolean>(false);
  const { hasAddedCard, setHasAddedCard } = useCardDetails();
  


    const meetingManager = useMeetingManager();
  


  const handleVideoTabClick = (tabName: string) => {
    setActiveVideoTabLabel(tabName);
  };

  const updateSearch = (event: any) => {
    setSearchTerm(event.target.value.trim().substr(0, 30));
  };

  const {
    getProviderAppointmentCount,
    isError: isProvApmntError,
    isLoading: isLoadProvApmnt,
    error: provApmntError
  } = useGetProviderAppointmentCount();

  const { providerAppointments, isLoading, isError, error } =
    useGetPatientUpcomingAppointments();
  const { data: patientData } = useGetSinglePatientRecord(patientId);

  const filteredData =
    providerAppointments?.providersUpcomingAppointments &&
    providerAppointments?.providersUpcomingAppointments?.filter(
      ({ patientName }) => {
        return (
          patientName?.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1
        );
      }
    );

  if (isError) {
    const message = isAxiosError(error)
      ? error?.response?.data?.error
      : "Error";
    toast.error(message, { toastId: "customId" });
  }

  if (isProvApmntError) {
    const message = isAxiosError(provApmntError)
      ? provApmntError?.response?.data?.error
      : "Error";
    toast.error(message, { toastId: "customId" });
  }

  const {
    getProviderPatientCount,
    isError: isProvPatientError,
    isLoading: isLoadProvPatient,
    error: provPatientError
  } = useGetProviderPatientsCount();

  if (isProvPatientError) {
    const message = isAxiosError(provPatientError)
      ? provPatientError?.response?.data?.error
      : "Error";
    toast.error(message, { toastId: "customId" });
  }

  useEffect(() => {
    if (providerAppointments?.providersUpcomingAppointments) {
      const appointmentHoldingNow = providerAppointments?.providersUpcomingAppointments?.filter((item: any) => {
        const appointmentDateTime = new Date(`${item.appointmentDate}T${item.appointmentStartTime}`);
        const now = new Date();
        const thirtyMinutesInMs = 30 * 60 * 1000;

        // Check if the appointment is happening now or within the next 30 minutes
        return appointmentDateTime.getTime() >= now.getTime() &&
          appointmentDateTime.getTime() - now.getTime() <= thirtyMinutesInMs;
      });

      setUpcomingAppointmentNow(appointmentHoldingNow);
    }
  }, [providerAppointments?.providersUpcomingAppointments]);

  useEffect(() => {
    if (didCallEnd) {
      setJoinCall(false);
      // setShowChargeSheetModal(true);
      setshowMeetingCostModal(true)
      setDidCallEnd(false);
    }
  }, [didCallEnd]);

  const handleCloseMeetingCostModal = () => {
    setshowMeetingCostModal(false);
    setShowChargeSheetModal(true);
  }

  const { saveFormResponse } = useSaveFormResponse();

  const { providerFormData } = useGetProviderForm({
    formType: appointmentInfo?.isInitialAppointment
      ? ["initialVisitNote"]
      : ["progressNote"],
    providerId: user?.userId ? user?.userId : ""
  });


  const {
    data,
    isError: isErr,
    error: err,
    isLoading: isLoad
  } = providerFormData[0];

  if (isErr && isAxiosError(err)) {
    toast.error(data?.error?.message, { toastId: "customId" });
  } else if (data?.error !== null) {
    toast.error(data?.error, { toastId: "customId" });
  } else {
    toast.error(
      data?.error?.message || "Error fetching provider's Intake Form",
      { toastId: "customId" }
    );
  }

  const { saveChargeSheet, isPending: isSavingDraft } =
    useSaveChargeSheetDraft();
  const { postChargeSheetToBilling, isPending: isSendingToBilling } =
    useSendChargeSheetToBilling();
  const {
    patientFormResponseData,
    isLoading: loading,
    refetchPatientFormResponse,
    isError: isPError,
    error: pError
  } = useGetPatientFormResponse({ appointmentId });

  if (isPError && isAxiosError(pError)) {
    toast.error(pError?.response?.data?.error, { toastId: "customId" });
  }

  useEffect(() => {
    if (appointmentId) {
      refetchPatientFormResponse();
    }
  }, [appointmentId ]);

  const { cancelAppointment } = useCancelAppointment();

  const { startAwsScribe } = useStartAwsScribe()

  // const checkThirtyMinutesBefore = useCallback(() => {
  //   if (upcomingAppointmentsNow.length === 0) {
  //     setIsThirtyMinutesBefore(false);
  //     return;
  //   }

  //   const appointment = upcomingAppointmentsNow[0];
  //   const { appointmentDate, appointmentStartTime } = appointment;

  //   // Combine appointment date and time into a single Date object
  //   const appointmentDateTime = new Date(`${appointmentDate}T${appointmentStartTime}`);

  //   // Get the current time
  //   const now = new Date();

  //   // Calculate the difference in minutes
  //   const diffInMinutes = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60);

  //   // Check if the appointment is happening now or within the next 30 minutes
  //   setIsThirtyMinutesBefore(diffInMinutes >= 0 && diffInMinutes <= 30);
  // }, [upcomingAppointmentsNow]);




  // const isWithinNext30Minutes = (appointmentDate: string, appointmentTime: string) => {
  //   const appointmentDateTime = new Date(`${appointmentDate}T${appointmentTime}`).getTime();
  //   const now = new Date().getTime();
  //   const diff = appointmentDateTime - now;
  //   return diff >= 0 && diff <= 30 * 60 * 1000;
  // };

  const isWithinNext30Minutes = (
    appointmentDate: string,
    appointmentTime: string,
    timeZone = 'America/New_York'
  ) => {
    const localDateTimeString = `${appointmentDate}T${appointmentTime}`;
    const appointmentLocal = toZonedTime(new Date(localDateTimeString), timeZone);
    const now = new Date();

    const diff = differenceInMilliseconds(appointmentLocal, now);
    return diff >= 0 && diff <= 30 * 60 * 1000;
  };

  useEffect(() => {
    if (providerAppointments?.providersUpcomingAppointments) {
      const filtered = providerAppointments.providersUpcomingAppointments.filter(({ appointmentStartTime, appointmentDate }) =>
        isWithinNext30Minutes(appointmentDate, appointmentStartTime)
      );
      setUpcomingAppointmentNow(filtered);
    }
  }, [providerAppointments?.providersUpcomingAppointments]);

  const checkThirtyMinutesBefore = useCallback(() => {
    if (upcomingAppointmentsNow.length === 0) {
      setIsThirtyMinutesBefore(false);
      return;
    }

    const { appointmentDate, appointmentStartTime } = upcomingAppointmentsNow[0];
    setIsThirtyMinutesBefore(isWithinNext30Minutes(appointmentDate, appointmentStartTime));
  }, [upcomingAppointmentsNow]);


  useEffect(() => {
    const interval = setInterval(checkThirtyMinutesBefore, 1000); // Check every second
    return () => clearInterval(interval);
  }, [checkThirtyMinutesBefore]);


  if (providerFormData[0].isLoading || isLoad || isLoading) {
    <div className="fixed inset-0 z-[10000000] backdrop-blur-sm flex justify-center w-full h-screen items-center">
      <Loading />
    </div>;
  }
  const { TABLE_COLUMNS } = useOverViewTableCols();

  const fullPatientData = useMemo(() => {
    return {
      appointmentId,
      patientName: `${patientData?.patientDetails.patient.firstName}${" "}${patientData?.patientDetails.patient.lastName}`,
      providerId: patientData?.patientDetails.patient?.userId,
      dateOfBirth: patientData?.patientDetails.patient.dateOfBirth,
      gender: patientData?.patientDetails.patient.gender,
      patientId: patientData?.patientDetails.patient.userId,
      insurance: patientData?.patientDetails.patient.insuranceName,
      modeOfEncounter: appointmentInfo?.appointmentType as
        | "virtual"
        | "physical",
      dateOfEncounter: appointmentInfo?.appointmentDate as string
    };
  }, [patientData, appointmentInfo, appointmentId]);  

  const adjustedAppointments =
    providerAppointments?.providersUpcomingAppointments.map(
      ({ appointmentDate, appointmentStartTime, appointmentEndTime, ...rest }) => {
        const startTimeParts = appointmentStartTime.split(":");
        const endTimeParts = appointmentEndTime.split(":");

        // Convert appointmentDate to user's timezone
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Get user's timezone
        const composeStartDate = toZonedTime(new Date(appointmentDate), timeZone);
        const composeEndDate = toZonedTime(new Date(appointmentDate), timeZone);

        // Adjust start and end times
        composeStartDate.setHours(composeStartDate.getHours() + Number(startTimeParts[0]), Number(startTimeParts[1]));
        composeEndDate.setHours(composeEndDate.getHours() + Number(endTimeParts[0]), Number(endTimeParts[1]));

        // Format dates
        const formatStartDate = formatInTimeZone(composeStartDate, timeZone, "yyyy/MM/dd HH:mm");
        const formatEndDate = formatInTimeZone(composeEndDate, timeZone, "yyyy-MM-dd HH:mm");

        return {
          ...rest,
          appointmentDate: formatStartDate.split(" ")[0],
          appointmentNewDate: formatStartDate.split(" ")[0],
          appointmentStartTime: formatStartDate.split(" ")[1],
          appointmentEndTime: formatEndDate.split(" ")[1],
        };
      }
    );

  const { providerData, isLoading: isLoadingProvider } = useGetProviderProfileDetails()

  useEffect(() => {
    if (!isLoadingProvider && providerData?.user?.hideIntroModal) {
      setIsIntroVideo(false)
    }
    // determine if provider already has a saved card (using presence of stripeCustomerId as proxy)
    
  }, [providerData, isLoadingProvider]);

  // Stripe card details React Query logic
  const { 
    isChecking, 
    error: stripeError, 
    customerId, 
    hasSavedCard,
    isCreatingCustomer,
    isCheckingCards 
  } = useStripeCardDetails({
    stripeCustomerId: user?.stripeCustomerId ?? undefined,
    setHasAddedCard,
  });

  const closeVideoModal = () => {
    setIsIntroVideo(false);
  };

  const { postMeeting, isPending, data: meetingData } = usePostMeeting()


  if (isPending) {
    return (
      <div className="fixed inset-0 z-[10000000] backdrop-blur-sm flex justify-center w-full h-full items-center">
        <Loading />
      </div>
    )
  }  

  return (
    <>
      {joinCall ? (
        <div className="h-full flex items-center " data-testid='join-call-container'>
          <TempleMeeting
            appointmentId={appointmentInfo?.appointmentId ?? ""}
            meetingResponse={meetingData?.meetingInfo || {
              Meeting: {
                ExternalMeetingId: "",
                MeetingId: "",
                MeetingArn: "",
                MediaRegion: "",
                MediaPlacement: {
                  AudioFallbackUrl: "",
                  AudioHostUrl: "",
                  SignalingUrl: "",
                  TurnControlUrl: "",
                },
                MeetingFeatures: {
                },
              }
            }}
            attendeeResponse={meetingData?.attendee || {
              Attendee: {
                AttendeeId: "",
                ExternalUserId: "",
                JoinToken: "",
                MediaRegion: "",
                MediaPlacement: {
                }
              }
            }}
            setFileName={setFileName}
            setNoteGenerationStatus={setNoteGenerationStatus}
            setDidCallEnd={setDidCallEnd}
            patientName={`${patientData?.patientDetails.patient.firstName}` || ""}
          />
          <div className="w-[40%] h-full px-4 py-5 flex flex-col justify-center items-center relative" data-testid="list-tabs-form-container">
            <div className="overflow-y-hidden mb-6 px-4 w-full overflow-x-scroll lg:overflow-x-hidden py-4" data-testid="tabs-container">
              <ListTabsWidget
                tabs={[{ label: "Notes" }, { label: "Intake Form" }]}
                handleTabClick={label => {
                  handleVideoTabClick(label);
                }}
                activeTabLabel={activeVideoTabLabel}
              />
            </div>
            {loading ? (
              <div className="flex justify-center items-center h-1/2">
                <Loading />
              </div>
            ) : (
              <>
                {activeVideoTabLabel === "Intake Form" && (
                  <div className="w-full h-full">
                    <div className="w-full flex justify-between items-center mb-6">
                      <p>
                        Intake form answers: {`${appointmentInfo?.patientName}`}
                      </p>
                    </div>
                    {patientFormResponseData?.formResponse?.values?.length ? (
                      <div className="w-full h-full">
                        {patientFormResponseData?.formResponse?.values?.map(
                          (item: any, idx: number) => (
                            <section key={idx} className="text-[18px]">
                              {Object.entries(item)?.map(([key, value]: any) => (
                                <div className="w-full pb-5" key={key}>
                                  <p className="font-semibold">{key}</p>
                                  {Array.isArray(value) ? (
                                    Object.keys(value[0])?.map(
                                      (item: any, index) => (
                                        <div key={index}>
                                          <label className="flex items-center gap-2">
                                            <input
                                              className="accent-[#3D874E]"
                                              type="checkbox"
                                              checked={value[0][item]}
                                              readOnly={true}
                                              aria-label={`${item} checkbox`}
                                            />
                                            <span>{item}</span>
                                          </label>
                                        </div>
                                      )
                                    )
                                  ) : (
                                    <p>{value}</p>
                                  )}
                                </div>
                              ))}
                            </section>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-1/2">
                        {patientFormResponseData?.message}
                      </div>
                    )}
                  </div>
                )}
                {activeVideoTabLabel === "Notes" && (
                  <>
                    {data?.initialVisitNote?.noteFields?.length ||
                      data?.progressNote?.noteFields?.length > 0 ? (
                      <div className="w-full h-full overflow-y-auto ">

                        {noteGenerationStatus === "ai-generation" && (
                          <div className="flex flex-col justify-center items-center">
                            <div className="w-full flex justify-start items-center text-pretty  mb-12">
                              <p>
                                Progress Note: {`${appointmentInfo?.patientName}`}
                              </p>
                            </div>
                            <div className="flex justify-center items-center w-full md:w-[450px] h-[250px] shadow-md bg-white rounded-[16px] border-box text-pretty">

                              <div className="flex flex-col px-2 py-2 text-pretty justify-center items-center">
                                <div className="w-full flex gap-3 justify-center items-center pb-10">
                                  <Playicon width={35} height={35} />
                                  <p>
                                    {`${appointmentInfo?.patientName}'s Progress Note`}
                                  </p>
                                </div>
                                <Button
                                  variant="primary"
                                  textColor="main"
                                  label="Generate documentation"
                                  onClick={() => {
                                    setShowAIPricingModal(true)
                                  }
                                  }
                                  size="large"
                                      additionalClassname="bg-[#3D874E] text-white rounded-lg px-4 py-2 text-pretty"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {noteGenerationStatus === "manual" && (
                          <div className="w-full h-full">
                            <p
                              className="pb-5 text-primary-900 text-lg cursor-pointer text-center underline"
                              onClick={() =>
                                setNoteGenerationStatus("ai-generation")
                              }
                            >
                                  Switch back to AI Generation?
                            </p>
                            <FormBuilder
                              formParams={
                                appointmentInfo?.isInitialAppointment
                                  ? data?.initialVisitNote?.noteFields ?? []
                                  : data?.progressNote?.noteFields ?? []
                              }
                              formType={
                                appointmentInfo?.isInitialAppointment
                                  ? "initialVisitNote"
                                  : "progressNote"
                              }
                              submitAction={() => {
                                saveFormResponse({
                                  noteType:
                                    appointmentInfo?.isInitialAppointment
                                      ? "initialVisitNote"
                                      : "progressNote",
                                  noteFields: [noteFields],
                                  appointmentId:
                                    appointmentInfo?.appointmentId ?? "",
                                  patientId: appointmentInfo?.patientId ?? ""
                                });
                              }}
                              isPreview={false}
                              formAnswers={noteFields}
                              setFormAnswers={setNoteFields}
                            />
                            
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>No notes!</div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <ProviderDashboardLayout>
          <div className="w-full h-full px-4 mt-4 lg:mt-16">
            <div className="flex flex-wrap lg:flex-nowrap items-center justify-start my-4 gap-4">
              <div className="w-full lg:w-[402px]">
                {isLoadProvApmnt ? (
                  <div className="flex justify-center items-center h-1/2">
                    <div className="flex justify-center items-center h-1/2">
                      <Loading />
                    </div>
                  </div>
                ) : (
                  <AppointmentsCountCard
                    appointmentCount={
                      getProviderAppointmentCount?.numberOfAppointmentsToday
                    }
                  />
                )}
              </div>
              <div className="w-4 hidden lg:block" />

              <div className="w-full mt-4 lg:mt-0 lg:w-[402px]">
                {isLoadProvPatient ? (
                  <div className="flex justify-center items-center h-1/2">
                    <div className="flex justify-center items-center h-1/2">
                      <Loading />
                    </div>
                  </div>
                ) : (
                  <PatientsCountCard
                    patientsCount={getProviderPatientCount?.numberOfPatients}
                  />
                )}
              </div>
              <div className="w-full lg:w-[402px]" data-testid="upcoming-appointments-card">
                {isThirtyMinutesBefore && upcomingAppointmentsNow?.length > 0 ? (
                  upcomingAppointmentsNow?.map((eventItem) => (
                    <EventCard
                      key={eventItem.id}
                      name={eventItem.patientName}
                      diagnosis={eventItem.diagnosis ?? "No Diagnosis"}
                      startTime={eventItem.appointmentStartTime}
                      endTime={eventItem.appointmentEndTime}
                      onClick={() => {
                        if (eventItem?.appointmentId) {
                          setAppointmentId(eventItem.appointmentId);
                          setPatientId(eventItem.patientId);
                        }
                        setAppointmentInfo(eventItem);
                        setOpenAppointmentDetails(true);
                        providerFormData[0].refetch();
                      }}
                    />
                  ))
                ) : (
                  <p className="w-full text-center text-[20px] text-[#324705] font-[500] lg:block" data-testid="empty-appointments-message">
                    No Events
                  </p>
                )}
              </div>

            </div>
            <div className="w-full" data-testid="upcoming-appointments-section-container">
              <p className="text-sm lg:text-[28px] text-[#324705] font-[600] py-4 ml-4" data-testid="upcoming-appointments-section">
                Upcoming Appointments
              </p>
              {providerAppointments?.providersUpcomingAppointments?.length ? (
                <Fragment>
                  <div className="hidden lg:block w-full my-4" data-testid='upcoming-appointments-table-section'>
                    {isLoadProvApmnt ? (<div className="flex justify-center items-center h-1/2">
                      <Loading />
                    </div>) : (
                      <ReusableTable
                        tableColumns={TABLE_COLUMNS}
                        tableData={
                          adjustedAppointments as UpcomingAppointmentType[]
                        }
                        onClick={data => {
                          if (data?.appointmentId) {
                            setAppointmentId(data?.appointmentId);
                            setPatientId(data?.patientId);
                          }
                          setAppointmentInfo(data);
                          setOpenAppointmentDetails(true);
                          providerFormData[0].refetch();
                        }}
                        searchFieldPlaceholder="Search Patient"
                      />
                    )}
                  </div>
                  <div className="w-fullblock lg:hidden  pb-2" data-testid="search-field-section">
                    <SearchField
                      placeholder="Search appointment"
                      value={searchTerm}
                      onChange={updateSearch}
                      name="searchTerm"
                    />
                  </div>
                  <div className="w-full block lg:hidden lg:mt-8 shadow-[-1.74129px_3.48259px_21.7662px_#EEEEE9]" data-testid="mobile-appointment-list">
                    <p className="w-full h-[53px] bg-[#EEEEE9] text-[#34623F] text-[500] flex items-center my-2 px-4">
                      Patients
                    </p>
                    {filteredData &&
                      filteredData?.map((item: any, index: number) => (
                        <MobileAppointmentCard
                          key={index}
                          appointmentDetails={item && item}
                        />
                      ))}
                  </div>
                </Fragment>
              ) : (
                <Fragment>
                  <div className="h-full w-full m-auto mt-[40px] flex flex-col justify-center items-center" data-testid="empty-appointments-container">
                    <EmptyMsgIcon data-testid="empty-msg-icon" />
                    <div className="font-semibold text-[#34623f] mt-4 text-center" data-testid="empty-appointments-message">
                      Nothing here
                    </div>
                    <div className="text-[#103C1B] pt-2 lg:w-[322px] text-center" data-testid="empty-appointments-submessage">
                          You don't have any upcoming appointments
                    </div>
                  </div>
                </Fragment>
              )}
            </div>
          </div>
          <ProviderAppointmentsDrawer
            setIsRescheduleViewVisible={setIsRescheduleViewVisible}
            setOldAppointmentForReschedule={setOldAppointmentForReschedule}
            open={openAppointmentDetails}
            onClose={() => {
              setOpenAppointmentDetails(false);
            }}
            drawerTitle="Appointment Details"
            appointmentDetails={appointmentInfo as Appointment}
            clickJoinCall={async () => {
              postMeeting({ appointmentId})
              await meetingManager.start(); // triggers device permission prompt for video chat
              setJoinCall(true)
              setOpenAppointmentDetails(false);
            }}
            patientIntakeFormResponse={patientFormResponseData?.formResponse}
            handleCancelAppointment={() => {
              cancelAppointment({
                appointmentId
              });
              setOpenAppointmentDetails(false);
              setAppointmentInfo(null);
            }}
            data-testid="provider-appointment-drawer"
          />
          <RescheduleAppointmentModal
            isVisible={isRescheduleViewVisible}
            closeModal={() => {
              setIsRescheduleViewVisible(false);
            }}
            oldId={oldAppointmentForReschedule}
          />
        </ProviderDashboardLayout>
      )}
      {showChargeSheetModal && (
        <div className="w-full h-[90%] lg:max-w-[90%] max-w-[95%] flex items-center justify-center border-box">
          <div className="w-full h-[90%] lg:max-w-[90%] max-w-[95%] my-8 mx-4 overflow-y-scroll rounded-tl-[16px]">
            <SimpleChargeSheetModal
              open={showChargeSheetModal}
              setOpen={setShowChargeSheetModal}
              chargeSheetData={fullPatientData}
              isSendingToBilling={isSendingToBilling}
              isSavingDraft={isSavingDraft}
              isFromNewBilling={false}
              saveAction={({
                patientId,
                appointmentId,
                procedureCodes,
                diagnosisCodes,
                modeOfEncounter,
                locationOfEncounter,
                dateOfEncounter
              }) => {
                saveChargeSheet({
                  patientId,
                  appointmentId,
                  procedureCodes,
                  diagnosisCodes,
                  status: "draft",
                  modeOfEncounter,
                  locationOfEncounter,
                  dateOfEncounter
                });
                setShowChargeSheetModal(false);
              }}
              sendToBillingAction={({
                patientId,
                appointmentId,
                procedureCodes,
                diagnosisCodes,
                modeOfEncounter,
                locationOfEncounter,
                dateOfEncounter
              }) => {
                postChargeSheetToBilling({
                  patientId,
                  appointmentId,
                  procedureCodes,
                  diagnosisCodes,
                  status: "processing",
                  modeOfEncounter,
                  locationOfEncounter,
                  dateOfEncounter
                });
                setShowChargeSheetModal(false);
              }}
            />
          </div>
        </div>
      )}
      {showMeetingCostModal && (<ChargesModal isVisible={showMeetingCostModal} appointmentInfo={appointmentInfo} onClose={handleCloseMeetingCostModal} appointmentId={appointmentId} />)}
      {showAIPricingModal && (
        <ModalPortal>
          <div
            style={{
              backgroundColor: "rgba(38, 38, 38, 0.75)",
              zIndex: 100,
              display: "flex",
              justifyContent: "center"
            }}
            className="w-screen h-screen fixed flex flex-col justify-center items-center top-0 bg-opacity-20 backdrop-blur-[5px]"
          >
            <div className="flex justify-end  w-[90%] lg:w-[462px] mx-auto py-2">
              <div
                onClick={() => {
                  setShowAIPricingModal(false);
                }}
                className="p-2 bg-white rounded-full cursor-pointer"
              >
                <CancelIcon className="h-full w-full" />
              </div>
            </div>
            <div className="bg-white w-[90%] lg:w-[462px] px-6 pt-6 pb-4 lg:p-6 shadow-lg rounded-[16px] border-box p-6">
              <p className="text-[#2E3011] text-[24px] font-[600] pb-3">
                AI Note Generation
              </p>
              <p className="mb-4 pt-3 text-[#2E3011] text-[16px] font-[400]">
                This service costs $0.10 per minute of transcription. Your saved card will be charged. <br />
              </p>
              <p className="text-[#103C1B] pb-3 text-[16px] font-[400]">Proceed?</p>

              <div className="flex justify-center gap-4">
                <Button
                  onClick={() => {
                    startAwsScribe({ fileName, appointmentId })
                    setShowAIPricingModal(false);
                    setNoteGenerationStatus("ai-generation");
                  }}
                  additionalClassname="bg-[#103C1B] text-white rounded-md px-4 py-2"
                  label="Yes, proceed"
                />

                <Button
                  onClick={() => {
                    setShowAIPricingModal(false);
                    setNoteGenerationStatus("manual");
                  }}
                  additionalClassname="bg-gray-300 rounded-md px-4 py-2"
                  label="No, use manual"
                />

              </div>
            </div>
          </div>
        </ModalPortal>
      )}
      {isIntroVideo && (
        <VideoModal isOpen={isIntroVideo} handleClose={closeVideoModal} />
      )}
    </>
  );
};

export default ProviderDashboardOverview;